import {IDocument} from 'types/documents';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';

export type FileState = {
    status: 'uploading' | 'uploaded' | 'error';
    file?: File;
    errorMsg?: string;
    document?: Pick<
        IDocument,
        '@id' | 'hash' | 'originalFileName' | 'mimeType'
    >;
};

type FileUploadStoreState = {
    files: Record<string, FileState>;
};

type FileUploadStoreActions = {
    addFile: (params: {
        file?: File;
        uuid: string;
        document?: IDocument;
        status?: FileState['status'];
        errorMsg?: string;
    }) => void;
    updateFileStatus: (params: {
        uuid: string;
        status: FileState['status'];
        document?: IDocument;
        errorMsg?: string;
    }) => void;
    removeFile: (uuid: string) => void;
    resetState: () => void;
};

const defaultStore: FileUploadStoreState = {
    files: {},
};

export const fileUploadStore = create(
    devtools(
        immer<FileUploadStoreState & FileUploadStoreActions>(set => ({
            ...defaultStore,
            addFile: ({file, uuid, document, status, errorMsg}) => {
                set(state => {
                    if (
                        !document ||
                        !Object.keys(state.files).some(
                            fileKey =>
                                state.files[fileKey].document?.['@id'] ===
                                document['@id'],
                        )
                    ) {
                        state.files[uuid] = {
                            file,
                            status: status || 'uploading',
                            document,
                            errorMsg,
                        };
                    }
                });
            },
            updateFileStatus: ({uuid, status, document, errorMsg}) => {
                set(state => {
                    state.files[uuid].status = status;
                    state.files[uuid].document = document;
                    state.files[uuid].errorMsg = errorMsg;
                });
            },
            removeFile: uuid => {
                set(state => {
                    const files = {...state.files};
                    delete files[uuid];
                    state.files = files;
                });
            },
            resetState: () => {
                set(state => {
                    state.files = {};
                });
            },
        })),
    ),
);
